/**
 * OfferCard — SONMI's offer to take responsibility, rendered.
 *
 * The offer is a single plain-language question with two answers. No
 * responsibility-level ladder is exposed to the user — that concept
 * has not yet been validated against real work, and exposing it makes
 * the offer feel like configuring software instead of SONMI naturally
 * taking work off the manager's plate.
 *
 * The offer sentence and judgment-reserved sentence come from the
 * server. This component is a faithful renderer.
 */
import React from "react";
import { sonmiCopy } from "@/lib/sonmiCopy";
import { Sparkles } from "lucide-react";

export function OfferCard({ offer, onGrant, onDecline, busy }) {
  if (!offer || offer.kind === "already_carrying") return null;

  return (
    <div
      className="rounded-2xl border border-stone-300 bg-gradient-to-br from-stone-50 to-white shadow-sm overflow-hidden"
      data-testid={`offer-card-${offer.employee.employee_id}`}
    >
      <div className="px-6 pt-5 pb-3 flex items-center gap-2 text-stone-500">
        <Sparkles className="w-4 h-4" />
        <span className="text-[11px] uppercase tracking-widest font-medium">
          {sonmiCopy.offer.heading}
        </span>
      </div>

      <div className="px-6 pb-5">
        <p
          className="text-[17px] leading-relaxed text-stone-800 whitespace-pre-line"
          data-testid={`offer-sentence-${offer.employee.employee_id}`}
        >
          {offer.offer_sentence}
        </p>

        {/* Judgment reserved — SONMI names when she WILL come back. */}
        <p className="mt-4 text-sm text-stone-500 italic"
           data-testid={`offer-judgment-${offer.employee.employee_id}`}>
          {offer.judgment_reserved_sentence}
        </p>
      </div>

      <div className="px-6 py-4 border-t border-stone-100 bg-stone-50/60 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDecline}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm text-stone-700 hover:bg-stone-100"
          data-testid={`offer-decline-${offer.employee.employee_id}`}
        >
          {sonmiCopy.offer.notYet}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onGrant}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300"
          data-testid={`offer-grant-${offer.employee.employee_id}`}
        >
          {busy ? "Saving…" : sonmiCopy.offer.yes}
        </button>
      </div>
    </div>
  );
}
